import { createContext, useContext, useEffect, useRef, useCallback, useState } from 'react';
import { useAuth } from './AuthContext';
import { useMessaging } from './MessagingContext';
import { toast } from 'react-toastify';
import { playTaskCreatedSound } from '../utils/taskSounds';
import api from '../services/api';

const NotificationContext = createContext(null);

export const useNotification = () => useContext(NotificationContext);

// Short descending beep for chat messages (unchanged)
const playNotificationSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.setValueAtTime(660, ctx.currentTime + 0.1);

    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);

    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.3);
    osc.onended = () => ctx.close();
  } catch (_) {}
};

export const NotificationProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const { socketRef, socketConnected } = useMessaging();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [postCallTarget, setPostCallTarget] = useState(null);
  const callbacksRef = useRef({});
  const userIdRef = useRef(user?.id);

  useEffect(() => { userIdRef.current = user?.id; }, [user?.id]);

  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const res = await api.get('/notifications');
      if (res.data.success) {
        setNotifications(res.data.notifications);
        setUnreadCount(res.data.notifications.filter(n => !n.isRead).length);
      }
    } catch (err) {
      console.error('[NotificationContext] fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const markAsRead = useCallback(async (id) => {
    try {
      const res = await api.patch(`/notifications/${id}/read`);
      if (res.data.success) {
        setNotifications(prev => prev.map(n => n._id === id ? { ...n, isRead: true } : n));
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('[NotificationContext] markAsRead error:', err);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      const res = await api.patch('/notifications/read-all');
      if (res.data.success) {
        setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
        setUnreadCount(0);
      }
    } catch (err) {
      console.error('[NotificationContext] markAllAsRead error:', err);
    }
  }, []);

  const registerCallback = useCallback((event, cb) => {
    if (!callbacksRef.current[event]) callbacksRef.current[event] = [];
    callbacksRef.current[event].push(cb);
    return () => {
      callbacksRef.current[event] = (callbacksRef.current[event] || []).filter((fn) => fn !== cb);
    };
  }, []);

  const fireCallbacks = useCallback((event, data) => {
    (callbacksRef.current[event] || []).forEach((cb) => {
      try { cb(data); } catch (_) {}
    });
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchNotifications();
    }
  }, [isAuthenticated, fetchNotifications]);

  // Attach listeners once the MessagingContext socket connects
  useEffect(() => {
    if (!socketConnected) return;
    const socket = socketRef.current;
    if (!socket) return;

    // ── Persistent Notification handler ──────────────────────────────────────
    const onNewNotification = (notification) => {
      setNotifications(prev => [notification, ...prev].slice(0, 50));
      setUnreadCount(prev => prev + 1);
      playNotificationSound();
      toast.info(notification.message);
    };

    // ── Chat messages ────────────────────────────────────────────────────────
    const onNewMessage = ({ message }) => {
      const senderId = String(message.senderId?._id || message.senderId);
      if (senderId !== String(userIdRef.current)) {
        playNotificationSound();
      }
    };

    // ── Pool task notifications ──────────────────────────────────────────────
    const onPoolNotification = ({ type, taskTitle, claimerName }) => {
      playNotificationSound();
      if (type === 'claim_request')  toast.info(`${claimerName} requested to claim "${taskTitle}"`);
      else if (type === 'approved')  toast.success(`Your claim on "${taskTitle}" was approved`);
      else if (type === 'rejected')  toast.warning(`Your claim on "${taskTitle}" was rejected`);
      else if (type === 'claimed')   toast.info(`${claimerName} claimed "${taskTitle}"`);
      else if (type === 'completed') toast.success(`${claimerName} completed "${taskTitle}"`);
    };

    // ── Granular task events (new) ───────────────────────────────────────────
    const onTaskCreated = (data) => {
      playTaskCreatedSound();
      fireCallbacks('task:created', data);
    };

    const onTaskUpdated = (data) => fireCallbacks('task:updated', data);

    const onTaskDeleted = (data) => fireCallbacks('task:deleted', data);

    // ── Legacy fallback ──────────────────────────────────────────────────────
    // Kept for pool task routes and any other paths that still emit task_update.
    const onTaskUpdate = (data) => fireCallbacks('task_update', data);

    const onPoolUpdate = (data) => fireCallbacks('pool_task_update', data);

    // ── Domain real-time events ──────────────────────────────────────────────
    const onProjectCreated = (data) => fireCallbacks('project:created', data);
    const onProjectUpdated = (data) => fireCallbacks('project:updated', data);
    const onProjectDeleted = (data) => fireCallbacks('project:deleted', data);
    const onClientCreated  = (data) => fireCallbacks('client:created',  data);
    const onClientUpdated  = (data) => fireCallbacks('client:updated',  data);
    const onClientDeleted  = (data) => fireCallbacks('client:deleted',  data);
    const onInvoiceCreated = (data) => fireCallbacks('invoice:created', data);
    const onInvoiceUpdated = (data) => fireCallbacks('invoice:updated', data);
    const onInvoiceDeleted = (data) => fireCallbacks('invoice:deleted', data);
    const onUserUpdated    = (data) => fireCallbacks('user:updated',    data);

    // ── Dialer events ────────────────────────────────────────────────────────
    const onDialerCallStarted = ({ agentId, contactName, phoneNumber, lead }) => {
      fireCallbacks('dialer:call_started', { agentId, contactName, phoneNumber, lead });
      // Toast for other agents watching (not the caller themselves)
      if (String(agentId) !== String(userIdRef.current)) {
        const who = contactName || phoneNumber || 'Unknown';
        toast.info(`📞 Work call started with ${who}`);
      }
    };

    // Only show the post-call form to the agent who handled the call
    const onDialerShowPostCallForm = (data) => {
      if (String(data.agentId) === String(userIdRef.current)) {
        setPostCallTarget(data);
      }
      fireCallbacks('dialer:show_post_call_form', data);
    };

    const onDialerRecordingReady = (data) => fireCallbacks('dialer:recording_ready', data);
    const onDialerCallEnded      = (data) => fireCallbacks('dialer:call_ended',       data);

    socket.on('new_notification',       onNewNotification);
    socket.on('new_message',            onNewMessage);
    socket.on('pool_task_notification', onPoolNotification);
    socket.on('pool_task_update',       onPoolUpdate);
    socket.on('task:created',           onTaskCreated);
    socket.on('task:updated',           onTaskUpdated);
    socket.on('task:deleted',           onTaskDeleted);
    socket.on('task_update',            onTaskUpdate);
    socket.on('project:created',        onProjectCreated);
    socket.on('project:updated',        onProjectUpdated);
    socket.on('project:deleted',        onProjectDeleted);
    socket.on('client:created',         onClientCreated);
    socket.on('client:updated',         onClientUpdated);
    socket.on('client:deleted',         onClientDeleted);
    socket.on('invoice:created',        onInvoiceCreated);
    socket.on('invoice:updated',        onInvoiceUpdated);
    socket.on('invoice:deleted',        onInvoiceDeleted);
    socket.on('user:updated',           onUserUpdated);
    socket.on('dialer:call_started',    onDialerCallStarted);
    socket.on('dialer:show_post_call_form', onDialerShowPostCallForm);
    socket.on('dialer:recording_ready', onDialerRecordingReady);
    socket.on('dialer:call_ended',      onDialerCallEnded);

    return () => {
      socket.off('new_notification',       onNewNotification);
      socket.off('new_message',            onNewMessage);
      socket.off('pool_task_notification', onPoolNotification);
      socket.off('pool_task_update',       onPoolUpdate);
      socket.off('task:created',           onTaskCreated);
      socket.off('task:updated',           onTaskUpdated);
      socket.off('task:deleted',           onTaskDeleted);
      socket.off('task_update',            onTaskUpdate);
      socket.off('project:created',        onProjectCreated);
      socket.off('project:updated',        onProjectUpdated);
      socket.off('project:deleted',        onProjectDeleted);
      socket.off('client:created',         onClientCreated);
      socket.off('client:updated',         onClientUpdated);
      socket.off('client:deleted',         onClientDeleted);
      socket.off('invoice:created',        onInvoiceCreated);
      socket.off('invoice:updated',        onInvoiceUpdated);
      socket.off('invoice:deleted',        onInvoiceDeleted);
      socket.off('user:updated',           onUserUpdated);
      socket.off('dialer:call_started',    onDialerCallStarted);
      socket.off('dialer:show_post_call_form', onDialerShowPostCallForm);
      socket.off('dialer:recording_ready', onDialerRecordingReady);
      socket.off('dialer:call_ended',      onDialerCallEnded);
    };
  }, [socketConnected, fireCallbacks]); // eslint-disable-line react-hooks/exhaustive-deps // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <NotificationContext.Provider value={{
      registerCallback,
      playNotificationSound,
      notifications,
      unreadCount,
      loading,
      markAsRead,
      markAllAsRead,
      refresh: fetchNotifications,
      postCallTarget,
      clearPostCallTarget: () => setPostCallTarget(null),
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

